<template>
  <EmptyStateCard
    v-if="loading || !entities.length"
    :message="loading ? t('loading') : t('emptyList')"
    class="min-h-20"
  >
    <template #icon>
      <LoadingSpinner v-if="loading" />
      <QueueListIcon v-else />
    </template>
  </EmptyStateCard>
  <template v-else>
    <Sort :sortOptions="sortOptions" @sort="onSort" />
    <div class="flex items-center justify-between">
      <div
        v-if="hasMultiselect && entities.length > 0"
        class="ml-2.5 rounded-md py-4"
      >
        <div class="flex h-6 items-center gap-x-2">
          <input
            id="selectAll"
            type="checkbox"
            class="ml-1 h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
            :checked="
              indeterminate ||
              (selected.length === entities.length && entities.length > 0)
            "
            :indeterminate="indeterminate"
            @change="
              selected = $event.target.checked ? entities.map((p) => p.id) : []
            "
          />
          <label for="selectAll" class="text-sm text-gray-700">{{
            selected.length === entities.length
              ? `${t('unselect')} ${selected.length} ${t('items')}`
              : t('selectAllOnPage')
          }}</label>
          <Menu
            v-if="selected.length > 0"
            as="div"
            class="relative ml-2 block text-left"
          >
            <div>
              <MenuButton
                class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100 focus:outline-hidden"
              >
                {{ t('candidates.actions.title') }}
                <ChevronDownIcon
                  class="-mr-1 ml-2 h-5 w-5"
                  aria-hidden="true"
                />
              </MenuButton>
            </div>

            <transition
              enterActiveClass="transition ease-out duration-100"
              enterFromClass="transform opacity-0 scale-95"
              enterToClass="transform opacity-100 scale-100"
              leaveActiveClass="transition ease-in duration-75"
              leaveFromClass="transform opacity-100 scale-100"
              leaveToClass="transform opacity-0 scale-95"
            >
              <MenuItems
                class="absolute right-0 z-10 mt-2 w-auto min-w-max origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden"
              >
                <div class="py-1">
                  <MenuItem
                    v-for="action in multiselectActions"
                    :key="action.value"
                    v-slot="{ active }"
                    @click="emitSelected(action.value)"
                  >
                    <a
                      href="#"
                      :class="[
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        'block px-4 py-2 text-sm',
                      ]"
                    >
                      <div class="flex items-center gap-x-2 text-gray-700">
                        <component :is="action.icon" class="w-5" />
                        <span v-html="action.label"></span>
                        <span>{{ `(${selected.length})` }}</span>
                      </div>
                    </a>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
      <div v-if="generateCSV" class="ml-auto py-4">
        <button
          class="inline-flex gap-x-1.5 rounded-sm border border-indigo-500 bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-600 shadow-xs hover:bg-indigo-100"
          :disabled="generatingCSV"
          @click="downloadCSV"
        >
          <ArrowPathIcon v-if="generatingCSV" class="h-4 w-4 animate-spin" />
          <TableCellsIcon v-else class="h-4 w-4" />
          {{ t('csv') }}
        </button>
      </div>
    </div>
    <div class="overflow-auto bg-white shadow-sm sm:rounded-md">
      <ul class="divide-y divide-gray-200">
        <li
          v-for="entity in entities"
          :key="entity.id"
          class="flex items-center hover:bg-gray-50"
        >
          <label class="absolute px-4 py-7">
            <input
              v-if="hasMultiselect"
              v-model="selected"
              type="checkbox"
              class="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
              :value="entity.id"
            />
          </label>
          <div class="basis-full" :class="{ 'ml-7': hasMultiselect }">
            <slot :entity="entity"></slot>
          </div>
        </li>
      </ul>
    </div>

    <Pagination
      v-if="page"
      :page="page"
      :updatePageNumber="updatePageNumber"
      :updatePageSize="updatePageSize"
      @page-updated="resetSelected"
    />
  </template>
</template>

<script setup>
import Pagination from './Pagination.vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon, QueueListIcon } from '@heroicons/vue/24/outline'
import { ArrowPathIcon, TableCellsIcon } from '@heroicons/vue/20/solid'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Sort from './Sort.vue'
import LoadingSpinner from './LoadingSpinner.vue'
import EmptyStateCard from './EmptyStateCard.vue'
import Papa from 'papaparse'

const props = defineProps({
  entities: {
    type: Array,
    required: true,
  },
  page: {
    type: Object,
    default: null,
  },
  updatePageNumber: {
    type: Function,
    default: () => {},
  },
  updatePageSize: {
    type: Function,
    default: () => {},
  },
  hasMultiselect: {
    type: Boolean,
    default: false,
  },
  multiselectActions: {
    type: Array,
    default: () => [],
  },
  sortOptions: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  generateCSV: {
    type: Function,
    default: undefined,
  },
})
const emit = defineEmits(['selectedAction', 'sort'])
const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const selected = ref([])
const generatingCSV = ref(false)

const indeterminate = computed(
  () =>
    selected.value.length > 0 && selected.value.length < props.entities.length,
)
const emitSelected = (actionName) => {
  emit('selectedAction', { actionName, selected })
  //reset after emit action
  selected.value = []
}
const resetSelected = () => {
  selected.value = []
}
const onSort = (params) => {
  emit('sort', { value: params.value, order: params.order })
}
const downloadCSV = async () => {
  if (props.generateCSV) {
    generatingCSV.value = true

    const { items, filename } = await props.generateCSV()
    const csv = Papa.unparse(items)

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)

    // We add the timestamp at the end of the filename
    const date = new Date()
    const offsetMs = date.getTimezoneOffset() * 60 * 1000
    const local = new Date(date.getTime() - offsetMs)
    const timestamp = local.toISOString().slice(0, 19)

    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}-${timestamp}.csv`
    link.click()

    URL.revokeObjectURL(url) // free memory
    generatingCSV.value = false
  }
}
</script>
